import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/gamesCss/List/deletionSingly.css"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/colors.css"
import { useLanguage } from "context/LanguageProvider";


const LinkedListDeletionGame = () => {
    const [currentLevel, setCurrentLevel] = useState(0);
    const [nodes, setNodes] = useState([]);
    const [headId, setHeadId] = useState(null);
    const [deletedNodes, setDeletedNodes] = useState([]);
    const [lives, setLives] = useState(3);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [gameComplete, setGameComplete] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [actionMode, setActionMode] = useState(null);
    const [reconnectFrom, setReconnectFrom] = useState(null);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [activeButton, setActiveButton] = useState(null);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const restartGame = () => {
        setCurrentLevel(0);
        setShowGameOver(false);
        setShowGameWon(false);
        setLives(3);
        loadLevel(0);
    }
    const LEVELS = [
        {
            id: 1,
            title: selLanguage
                ? "Επίπεδο 1: Διαγραφή Πρώτου Κόμβου"
                : "Level 1: Delete First Node",
            description: selLanguage
                ? "Διέγραψε τον πρώτο κόμβο από τη λίστα."
                : "Delete the first node from the list.",
            initialList: [
                { id: "A", value: "10", next: "B" },
                { id: "B", value: "20", next: "C" },
                { id: "C", value: "30", next: "D" },
                { id: "D", value: "40", next: null }
            ],
            headId: "A",
            deleteTarget: "A",
            expectedHead: "B",
            expectedList: [
                { id: "B", value: "20", next: "C" },
                { id: "C", value: "30", next: "D" },
                { id: "D", value: "40", next: null }
            ]
        },
        {
            id: 2,
            title: selLanguage
                ? "Επίπεδο 2: Διαγραφή Τελευταίου Κόμβου"
                : "Level 2: Delete Last Node",
            description: selLanguage
                ? "Διέγραψε τον τελευταίο κόμβο από τη λίστα."
                : "Delete the last node from the list.",
            initialList: [
                { id: "X", value: "5", next: "Y" },
                { id: "Y", value: "15", next: "Z" },
                { id: "Z", value: "25", next: "W" },
                { id: "W", value: "35", next: null }
            ],
            headId: "X",
            deleteTarget: "W",
            expectedHead: "X",
            expectedList: [
                { id: "X", value: "5", next: "Y" },
                { id: "Y", value: "15", next: "Z" },
                { id: "Z", value: "25", next: null }
            ]
        },
        {
            id: 3,
            title: selLanguage
                ? "Επίπεδο 3: Διαγραφή Ενδιάμεσου Κόμβου"
                : "Level 3: Delete Middle Node",
            description: selLanguage
                ? "Διέγραψε τον κόμβο R από τη λίστα."
                : "Delete node R from the list.",
            initialList: [
                { id: "P", value: "100", next: "Q" },
                { id: "Q", value: "200", next: "R" },
                { id: "R", value: "300", next: "S" },
                { id: "S", value: "400", next: "T" },
                { id: "T", value: "500", next: null }
            ],
            headId: "P",
            deleteTarget: "R",
            expectedHead: "P",
            expectedList: [
                { id: "P", value: "100", next: "Q" },
                { id: "Q", value: "200", next: "S" },
                { id: "S", value: "400", next: "T" },
                { id: "T", value: "500", next: null }
            ]
        },
        {
            id: 4,
            title: selLanguage
                ? "Επίπεδο 4: Διπλή Διαγραφή - Πρώτος και Μεσαίος"
                : "Level 4: Double Deletion – First and Middle",
            description: selLanguage
                ? "Διέγραψε το πρώτο στοιχείο και τον κόμβο Ο από τη λίστα."
                : "Delete the first element and node O from the list.",
            initialList: [
                { id: "M", value: "50", next: "N" },
                { id: "N", value: "60", next: "O" },
                { id: "O", value: "70", next: "V" },
                { id: "V", value: "80", next: "U" },
                { id: "U", value: "90", next: null }
            ],
            headId: "M",
            deleteTargets: ["M", "O"],
            expectedHead: "N",
            expectedList: [
                { id: "N", value: "60", next: "V" },
                { id: "V", value: "80", next: "U" },
                { id: "U", value: "90", next: null }
            ]
        },
        {
            id: 5,
            title: selLanguage
                ? "Επίπεδο 5: Τριπλή Διαγραφή - Ultimate Challenge!"
                : "Level 5: Triple Deletion – Ultimate Challenge!",
            description: selLanguage
                ? "Διέγραψε τον πρώτο κόμβο, τον D από τη μέση και τον τελευταίο κόμβο! Πρόσεχε η τελική απάντηση αν είναι ολοκληρωμένη."
                : "Delete the first node, node D from the middle, and the last node! Make sure your final answer is fully correct.",
            initialList: [
                { id: "A", value: "1", next: "B" },
                { id: "B", value: "2", next: "C" },
                { id: "C", value: "3", next: "D" },
                { id: "D", value: "4", next: "E" },
                { id: "E", value: "5", next: "F" },
                { id: "F", value: "6", next: null }
            ],
            headId: "A",
            deleteTargets: ["A", "D", "F"],
            expectedHead: "B",
            expectedList: [
                { id: "B", value: "2", next: "C" },
                { id: "C", value: "3", next: "E" },
                { id: "E", value: "5", next: null }
            ]
        }
    ];


    useEffect(() => {
        loadLevel(currentLevel);
    }, [currentLevel]);

    const loadLevel = (levelIndex) => {
        const level = LEVELS[levelIndex];
        setNodes(JSON.parse(JSON.stringify(level.initialList)));
        setHeadId(level.headId);
        setDeletedNodes([]);
        setShowSuccess(false);
        setShowError(false);
        setErrorMessage("");
        setSelectedNode(null);
        setActionMode(null);
        setReconnectFrom(null);
        setShowGameOver(false);
    };

    const resetLevel = () => {
        loadLevel(currentLevel);
    };

    const handleMoveHead = (nodeId) => {
        setHeadId(nodeId);
        setSelectedNode(null);
    };

    const handleDeleteNode = (nodeId) => {
        // ΔΕΝ μετακινούμε αυτόματα το HEAD!
        setDeletedNodes([...deletedNodes, nodeId]);
        setNodes(nodes.filter(n => n.id !== nodeId));

        // Αν διαγράφεται ο κόμβος που δείχνει το HEAD, το HEAD γίνεται null
        if (headId === nodeId) {
            setHeadId(null);
        }

        setSelectedNode(null);
    };

    const handleReconnect = (fromId, toId) => {
        setNodes(nodes.map(node =>
            node.id === fromId ? { ...node, next: toId } : node
        ));
        setReconnectFrom(null);
        setSelectedNode(null);
    };

    const checkSolution = () => {
        const level = LEVELS[currentLevel];

        // Check head
        if (headId !== level.expectedHead) {
            const newLives = lives - 1;
            setErrorMessage(selLanguage ? `Κάπου υπάρχει ένα λάθος! Συνέχισε την καλή προσπάθεια!` : ` There's a mistake somewhere! Keep up the good effort!`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setShowGameOver(true);
            }

            setTimeout(() => setShowError(false), 2500); // Αλλαγή σε 2500ms
            return;
        }

        // Check deleted nodes
        const expectedDeletedIds = level.deleteTargets || [level.deleteTarget];
        const sortedDeleted = [...deletedNodes].sort();
        const sortedExpected = [...expectedDeletedIds].sort();

        if (JSON.stringify(sortedDeleted) !== JSON.stringify(sortedExpected)) {
            const newLives = lives - 1;
            setErrorMessage(selLanguage ? `Κάπου υπάρχει ένα λάθος! Συνέχισε την καλή προσπάθεια!` : ` There's a mistake somewhere! Keep up the good effort!`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setShowGameOver(true);
            }

            setTimeout(() => setShowError(false), 2500); // Αλλαγή σε 2500ms
            return;
        }

        // Check remaining list structure
        const currentIds = nodes.map(n => n.id).sort();
        const expectedIds = level.expectedList.map(n => n.id).sort();

        if (JSON.stringify(currentIds) !== JSON.stringify(expectedIds)) {
            const newLives = lives - 1;
            setErrorMessage(selLanguage ? `Κάπου υπάρχει ένα λάθος! Συνέχισε την καλή προσπάθεια!` : ` There's a mistake somewhere! Keep up the good effort!`);
            setShowError(true);
            setLives(newLives);

            if (newLives === 0) {
                setShowGameOver(true);
            }

            setTimeout(() => setShowError(false), 2500); // Αλλαγή σε 2500ms
            return;
        }

        // Check connections
        for (const node of nodes) {
            const expected = level.expectedList.find(n => n.id === node.id);
            if (node.next !== expected.next) {
                const newLives = lives - 1;
                setErrorMessage(selLanguage ? `Κάπου υπάρχει ένα λάθος! Συνέχισε την καλή προσπάθεια!` : ` There's a mistake somewhere! Keep up the good effort!`);
                setShowError(true);
                setLives(newLives);

                if (newLives === 0) {
                    setShowGameOver(true);
                }

                setTimeout(() => setShowError(false), 2500); // Αλλαγή σε 2500ms
                return;
            }
        }

        setShowSuccess(true);
        setTimeout(() => {
            setShowSuccess(false);
            if (currentLevel < LEVELS.length - 1) {
                setCurrentLevel(prev => prev + 1);
                setLives(3);
            } else {
                setShowGameWon(true);
            }
        }, 1500);
    };

    const handleNodeClick = (nodeId) => {
        if (actionMode === 'move-head') {
            handleMoveHead(nodeId);
        } else if (actionMode === 'delete') {
            handleDeleteNode(nodeId);
        } else if (actionMode === 'reconnect') {
            if (!reconnectFrom) {
                setReconnectFrom(nodeId);
            } else if (reconnectFrom !== nodeId) {
                handleReconnect(reconnectFrom, nodeId === 'NULL' ? null : nodeId);
            }
        }
    };

    const getNodePosition = (index) => {
        const startX = 80;
        const spacing = 140;
        return { x: startX + index * spacing, y: 200 };
    };

    const buttonStyle = {
        padding: '16px 24px',
        borderRadius: '16px',
        border: 'none',
        fontSize: '1rem',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        fontFamily: 'inherit',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        background: darkMode ? "#1e293b" : "var(--customBuildButton)",
        fontFamily: "Poppins, sans-serif",
        color: "#7a8effff",
        border: "2px solid #7a8effff"
    }


    const level = LEVELS[currentLevel];
    const deleteTargets = level.deleteTargets || [level.deleteTarget];

    return (
        <div
            className="singlyDelContainer main-content"
            style={{
                background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)",
                width: "100%",
            }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={restartGame}
            />


            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={restartGame}
            />

            {/* Header */}
            <div className="singlyDelHeader">
                <div style={{
                    color: darkMode ? "white" : "#1e293b",
                    padding: "12px 24px",
                    borderRadius: "12px",
                    fontWeight: "700",
                    fontSize: "18px",
                    border: darkMode ? "2px solid white" : "2px solid #1e293b",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                }}>

                    {selLanguage ? "Επίπεδο" : "Level"} {level.id}/{LEVELS.length}

                </div>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "row",
                    justifyContent: "right",
                    gap: "12px"
                }}>
                    <div style={{
                        display: "flex",
                        gap: "6px",
                        alignItems: "center"
                    }}>
                        {[1, 2, 3].map((heartNum) => (
                            <div
                                key={heartNum}
                                style={{
                                    fontSize: "24px",
                                    filter: lives >= heartNum
                                        ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                        : "grayscale(100%) opacity(0.3)",
                                    transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                    transition: "all 0.3s ease",
                                    animation: lives >= heartNum ? "heartbeat 2s ease-in-out infinite" : "none"
                                }}
                            >
                                ❤️
                            </div>
                        ))}
                    </div>


                </div>
            </div>

            {/* Instructions */}
            <div className="singlyDelProm">
                <p style={{ color: '#ffffffff', marginBottom: '15px', lineHeight: '1.6' }}>{level.description}</p>
            </div>

            {/* Action Buttons */}
            <div className="SinglyDelActionbtn">
                <button
                    className="singlyDelButtons"
                    onClick={() => {
                        const isActive = activeButton === 'move-head';
                        setActionMode(isActive ? null : 'move-head');
                        setActiveButton(isActive ? null : 'move-head');
                    }}
                    style={{
                        ...buttonStyle,
                        ...(activeButton === 'move-head' && {
                            background: "#7a8eff",
                            color: "white",
                            border: "2px solid #4751d6",

                        })
                    }}
                >
                    {selLanguage ? "Μετακίνηση HEAD" : "Move HEAD"}
                </button>

                <button
                    className="singlyDelButtons"
                    onClick={() => {
                        const isActive = activeButton === 'reconnect';
                        setActionMode(isActive ? null : 'reconnect');
                        setActiveButton(isActive ? null : 'reconnect');
                        setReconnectFrom(null);
                    }}
                    style={{
                        ...buttonStyle,
                        ...(activeButton === 'reconnect' && {
                            background: "#7a8eff",
                            color: "white",
                            border: "2px solid #4751d6",

                        })
                    }}
                >
                    {selLanguage ? "Σύνδεση Κόμβων" : "Connect nodes"}
                </button>

                <button
                    className="singlyDelButtons"
                    onClick={() => {
                        const isActive = activeButton === 'delete';
                        setActionMode(isActive ? null : 'delete');
                        setActiveButton(isActive ? null : 'delete');
                    }}
                    style={{
                        ...buttonStyle,
                        ...(activeButton === 'delete' && {
                            background: "#7a8eff",
                            color: "white",
                            border: "2px solid #4751d6",
                        })
                    }}
                >
                    {selLanguage ? "Διαγραφή Κόμβου" : "Delete node"}
                </button>

            </div>

            {
                reconnectFrom && (
                    <div style={{
                        background: '#dbeafe',
                        padding: '12px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        marginBottom: '20px',
                        border: '2px solid #3b82f6'
                    }}>
                        {selLanguage ? `Επέλεξε τον κόμβο στον οποίο θέλεις να συνδέσεις το ${reconnectFrom}` : `Select the node to connect ${reconnectFrom} to`}
                    </div>
                )
            }

            {/* Linked List Visualization */}
            <div style={{
                background: 'white',
                borderRadius: '15px',
                padding: '40px 20px',
                marginBottom: '20px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                overflow: 'auto'
            }}>
                <svg width="100%" height="400" viewBox="0 0 1000 400">
                    <defs>
                        <marker
                            id="arrowhead"
                            markerWidth="10"
                            markerHeight="10"
                            refX="9"
                            refY="3"
                            orient="auto"
                        >
                            <polygon points="0 0, 10 3, 0 6" fill="#3b82f6" />
                        </marker>
                    </defs>

                    {/* Draw connections - ΣΥΜΠΕΡΙΛΑΜΒΑΝΟΝΤΑΙ ΚΑΙ ΟΙ ΣΥΝΔΕΣΕΙΣ ΣΤΟ NULL */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);

                        if (node.next) {
                            const nextNode = nodes.find(n => n.id === node.next);
                            if (nextNode) {
                                const nextIndex = nodes.indexOf(nextNode);
                                const nextPos = getNodePosition(nextIndex);

                                return (
                                    <motion.line
                                        key={`edge-${node.id}`}
                                        x1={pos.x + 60}
                                        y1={pos.y}
                                        x2={nextPos.x - 10}
                                        y2={nextPos.y}
                                        stroke="#3b82f6"
                                        strokeWidth="3"
                                        markerEnd="url(#arrowhead)"
                                        initial={{ pathLength: 0 }}
                                        animate={{ pathLength: 1 }}
                                        transition={{ duration: 0.5 }}
                                    />
                                );
                            }
                        } else {
                            // ΣΥΝΔΕΣΗ ΣΤΟ NULL
                            const nullPos = getNodePosition(nodes.length);
                            return (
                                <motion.line
                                    key={`edge-${node.id}-null`}
                                    x1={pos.x + 60}
                                    y1={pos.y}
                                    x2={nullPos.x}
                                    y2={nullPos.y}
                                    stroke="#3b82f6"
                                    strokeWidth="3"
                                    markerEnd="url(#arrowhead)"
                                    strokeDasharray="5,5"
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: 1 }}
                                    transition={{ duration: 0.5 }}
                                />
                            );
                        }
                        return null;
                    })}

                    {/* Draw HEAD indicator */}
                    {headId && nodes.find(n => n.id === headId) && (
                        <motion.g
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: -15, opacity: 1 }}
                        >
                            <text
                                x={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 30}
                                y={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 40}
                                textAnchor="middle"
                                fill="#10b981"
                                fontSize="20"
                                fontWeight="700"
                            >
                                HEAD
                            </text>
                            <line
                                x1={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 30}
                                y1={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 30}
                                x2={getNodePosition(nodes.findIndex(n => n.id === headId)).x + 30}
                                y2={getNodePosition(nodes.findIndex(n => n.id === headId)).y - 10}
                                stroke="#10b981"
                                strokeWidth="3"
                                markerEnd="url(#arrowhead)"
                            />
                        </motion.g>
                    )}

                    {/* Draw nodes */}
                    {nodes.map((node, index) => {
                        const pos = getNodePosition(index);
                        const isTarget = deleteTargets.includes(node.id);

                        return (
                            <motion.g
                                key={node.id}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <rect
                                    x={pos.x}
                                    y={pos.y - 25}
                                    width="60"
                                    height="50"
                                    fill={"#eff6ff"}
                                    stroke={"#3b82f6"}
                                    strokeWidth="3"
                                    rx="8"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => handleNodeClick(node.id)}
                                />
                                <text
                                    x={pos.x + 30}
                                    y={pos.y - 5}
                                    textAnchor="middle"
                                    fill={"#1e293b"}
                                    fontSize="14"
                                    fontWeight="700"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {node.id}
                                </text>
                                <text
                                    x={pos.x + 30}
                                    y={pos.y + 12}
                                    textAnchor="middle"
                                    fill="#64748b"
                                    fontSize="12"
                                    style={{ pointerEvents: 'none' }}
                                >
                                    {node.value}
                                </text>

                                {/* Next pointer indicator */}
                                <circle
                                    cx={pos.x + 55}
                                    cy={pos.y}
                                    r="5"
                                    fill={"#3b82f6"}
                                />
                            </motion.g>
                        );
                    })}

                    {/* NULL box */}
                    {nodes.length > 0 && (
                        <g>
                            <rect
                                x={getNodePosition(nodes.length).x}
                                y={getNodePosition(nodes.length).y - 15}
                                width="50"
                                height="30"
                                fill="#1f2937"
                                stroke="#64748b"
                                strokeWidth="2"
                                rx="5"
                                style={{ cursor: actionMode === 'reconnect' && reconnectFrom ? 'pointer' : 'default' }}
                                onClick={() => actionMode === 'reconnect' && reconnectFrom && handleNodeClick('NULL')}
                            />
                            <text
                                x={getNodePosition(nodes.length).x + 25}
                                y={getNodePosition(nodes.length).y + 5}
                                textAnchor="middle"
                                fill="#94a3b8"
                                fontSize="14"
                                fontWeight="700"
                                style={{ pointerEvents: 'none' }}
                            >
                                NULL
                            </text>
                        </g>
                    )}
                </svg>
            </div>

            {/* Deleted Nodes Section */}
            {
                deletedNodes.length > 0 && (
                    <div style={{
                        background: '#fee2e2',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #dc2626'
                    }}>
                        <h3 style={{ color: '#dc2626', marginBottom: '15px' }}>{selLanguage ? "Διαγραμμένοι Κόμβοι" : "Deleted Nodes"}</h3>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {deletedNodes.map((nodeId, index) => (
                                <motion.div
                                    key={nodeId}
                                    style={{
                                        background: '#dc2626',
                                        color: 'white',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        fontWeight: 'bold',
                                        fontSize: '16px'
                                    }}
                                    initial={{ scale: 0, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    {nodeId}
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )
            }

            {/* Success Message */}
            {showSuccess && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#d1fae5',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #10b981',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <p style={{ color: '#065f46', margin: 0 }}>
                        {selLanguage ? "Συγχαρητήρια! Προχωράς στο επόμενο επίπεδο!" : "Congratulations! Moving to the next level!"}
                    </p>
                </motion.div>
            )}

            {/* Error Message */}
            {showError && (
                <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    style={{
                        background: '#fee2e2',
                        borderRadius: '15px',
                        padding: '20px',
                        marginBottom: '20px',
                        border: '2px solid #dc2626',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >

                    <p style={{ color: '#991b1b', margin: 0 }}>
                        {errorMessage}
                    </p>
                </motion.div>
            )}

            {/* Control Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                    onClick={checkSolution}
                    style={{
                        background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                        color: "white",
                        border: "none",
                        borderRadius: "16px",
                        padding: "14px 28px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.2)",
                        transition: "all 0.3s ease",
                        transform: "scale(1)",
                        marginRight: "12px"
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = "scale(1.05)";
                    }
                    }
                    onMouseLeave={(e) => {
                        e.target.style.transform = "scale(1)";
                    }}

                >
                    {selLanguage ? "Έλεγχος Λύσης" : "Check solution"}
                </button>
                <button
                    onClick={resetLevel}
                    style={{
                        background: "linear-gradient(135deg, #b81e1eff 0%, #7c0505ff 100%)",
                        color: "white",
                        border: "none",
                        borderRadius: "16px",
                        padding: "14px 28px",
                        fontSize: "1rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        boxShadow: "0 10px 25px rgba(184, 30, 30, 0.3), 0 0 0 1px rgba(184, 30, 30, 0.2)",
                        transition: "all 0.3s ease",
                        transform: "scale(1)",
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.transform = "scale(1.05)";
                    }
                    }
                    onMouseLeave={(e) => {
                        e.target.style.transform = "scale(1)";
                    }}
                >
                    {selLanguage ? "Επαναφορά" : "Restart"}
                </button>
            </div>
        </div >
    );
};

export default LinkedListDeletionGame;